import { useState } from 'react';
import { type LucideIcon, Newspaper, ArrowUpRight, Landmark, ShieldCheck, Globe, Building2, Scale, RefreshCw } from 'lucide-react';
import { NEWS } from '../lib/news';
import { NEWS_GRADIENTS } from '../lib/tokens';
import { cn } from './ui';

function shortDate(iso: string): string {
  return new Intl.DateTimeFormat('es-PA', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'hoy';
  if (days === 1) return 'ayer';
  if (days < 30) return `hace ${days} días`;
  const months = Math.floor(days / 30);
  return months === 1 ? 'hace 1 mes' : `hace ${months} meses`;
}

// Designed cover art (CSP-safe, no external images): maroon-family gradients + a topical icon.
const GRADIENTS = NEWS_GRADIENTS;

const SOURCE_ICON: Record<string, LucideIcon> = {
  SBP: Landmark,
  UAF: ShieldCheck,
  GAFI: Globe,
  Intendencia: Building2,
  MEF: Scale,
};

/** Instagram-style horizontal news rail with designed cover art. */
export function NewsWidget() {
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(NEWS[0]?.dateISO ?? '');

  function handleRefresh() {
    if (refreshing) return;
    setRefreshing(true);
    setTimeout(() => {
      setRefreshing(false);
      setLastRefreshed(new Date().toISOString().slice(0, 10));
    }, 1400);
  }

  return (
    <section data-tour="home-novedades">
      <div className="mb-4 flex items-end justify-between">
        <div className="flex items-center gap-2.5">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary-dark">
            <Newspaper className="h-4 w-4" strokeWidth={1.75} />
          </span>
          <h2 className="font-serif text-2xl font-medium text-ink">Novedades de cumplimiento</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] text-ink-faint">
            Actualizado {relativeTime(lastRefreshed)}
          </span>
          <button
            onClick={handleRefresh}
            aria-label="Refrescar novedades"
            disabled={refreshing}
            className="grid h-7 w-7 place-items-center rounded-full text-ink-faint transition-colors hover:bg-canvas hover:text-ink disabled:opacity-50"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} strokeWidth={2} />
          </button>
        </div>
      </div>

      {refreshing ? (
        <div className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex w-72 shrink-0 snap-start flex-col overflow-hidden rounded-3xl border border-line bg-panel shadow-card">
              <div className="h-32 animate-pulse bg-line" />
              <div className="flex flex-col gap-2 p-4">
                <div className="h-3.5 w-3/4 animate-pulse rounded bg-line" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-line" />
                <div className="mt-2 h-3 w-1/4 animate-pulse rounded bg-line" />
              </div>
            </div>
          ))}
          <span className="w-4 shrink-0 sm:w-6" aria-hidden="true" />
        </div>
      ) : (
        <div className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {NEWS.map((n, i) => {
            const [from, to] = GRADIENTS[i % GRADIENTS.length]!;
            const Icon = SOURCE_ICON[n.source] ?? Newspaper;
            return (
              <a
                key={i}
                href={n.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex w-72 shrink-0 snap-start flex-col overflow-hidden rounded-3xl border border-line bg-panel shadow-card transition-all hover:-translate-y-0.5 hover:shadow-pop"
              >
                <div
                  className="relative h-32 overflow-hidden"
                  style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}
                >
                  <Icon className="absolute -bottom-4 -right-3 h-28 w-28 text-white/15" strokeWidth={1.25} />
                  <span className="absolute left-3 top-3 rounded-md bg-white/15 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wide text-white backdrop-blur-sm">
                    {n.source}
                  </span>
                  <ArrowUpRight className="absolute right-3 top-3 h-4 w-4 text-white/70 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <p className="line-clamp-3 text-sm font-semibold leading-snug text-ink">{n.title}</p>
                  <p className="mt-auto pt-3 font-mono text-[10.5px] text-ink-faint">{shortDate(n.dateISO)}</p>
                </div>
              </a>
            );
          })}
          <span className="w-4 shrink-0 sm:w-6" aria-hidden="true" />
        </div>
      )}
    </section>
  );
}
